<template>
  <div>
    <transition
      name="fade"
      mode="out-in"
    >
      <div v-if="!isActionPending">
        <b-row>
          <b-col cols="12">
            <p>
              <!-- prettier-ignore -->
              <span class="mr-1 font-weight-bold">{{ $t('edit-community-modal.subscription.subscription-info.subscription-status') }}:</span>
              <b-badge :variant="subscriptionStatusBadgeVariant">
                {{ $t(`edit-community-modal.subscription.subscription-status.${_.toLower(subscription.status)}`) }}
              </b-badge>
            </p>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <b-alert
              :show="isCancelled"
              variant="warning"
              dismissible
            >
              <div class="alert-body">
                {{ $t('edit-community-modal.subscription.subscription-info.cancelled-disclamer') }}
              </div>
            </b-alert>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <p>
              <span class="mr-25 font-weight-bold">
                {{ $t('edit-community-modal.subscription.subscription-info.subscription-date') }}:
              </span>
              <span v-b-tooltip.hover="moment(subscription.createdAt).format('LLL')">
                {{ moment(subscription.createdAt).format('YYYY-MM-DD') }} ({{
                  moment(subscription.createdAt).fromNow()
                }})
              </span>
            </p>
          </b-col>
        </b-row>
        <b-row v-if="isActive">
          <b-col cols="12">
            <p>
              <span class="mr-25 font-weight-bold">
                {{ $t('edit-community-modal.subscription.subscription-info.renewal-date') }}:
              </span>
              <span v-b-tooltip.hover="moment(subscription.nextBillingTime).format('LLL')">
                {{ moment(subscription.nextBillingTime).format('YYYY-MM-DD') }} ({{
                  moment(subscription.nextBillingTime).fromNow()
                }})
              </span>
            </p>
          </b-col>
        </b-row>
        <!-- eslint-disable -->
        <b-row v-if="(isPaused || isCancelled || isPastDue) && moment(subscription.billingCycleEndDate).isAfter(moment())">
          <!-- eslint-enable -->
          <b-col cols="12">
            <p>
              <span class="mr-25 font-weight-bold">
                {{ $t('edit-community-modal.subscription.subscription-info.active-util') }}:
              </span>
              <span v-b-tooltip.hover="moment(subscription.billingCycleEndDate).format('LLL')">
                {{ moment(subscription.billingCycleEndDate).format('YYYY-MM-DD') }} ({{
                  moment(subscription.billingCycleEndDate).fromNow()
                }})
              </span>
            </p>
          </b-col>
        </b-row>
        <b-row v-if="isPaused">
          <b-col cols="12">
            <p>
              <span class="mr-25 font-weight-bold">
                {{ $t('edit-community-modal.subscription.subscription-info.suspension-date') }}:
              </span>
              <span v-b-tooltip.hover="moment(subscription.pausedAt).format('LLL')">
                {{ moment(subscription.pausedAt).format('YYYY-MM-DD') }} ({{ moment(subscription.pausedAt).fromNow() }})
              </span>
            </p>
          </b-col>
        </b-row>
        <b-row v-if="isPastDue">
          <b-col cols="12">
            <p>
              <span class="mr-25 font-weight-bold">
                {{ $t('edit-community-modal.subscription.subscription-info.expiration-date') }}:
              </span>
              <span v-b-tooltip.hover="moment(subscription.expiredAt).format('LLL')">
                {{ moment(subscription.expiredAt).format('YYYY-MM-DD') }} ({{
                  moment(subscription.expiredAt).fromNow()
                }})
              </span>
            </p>
          </b-col>
        </b-row>
        <b-row v-if="isCancelled">
          <b-col cols="12">
            <p>
              <span class="mr-25 font-weight-bold">
                {{ $t('edit-community-modal.subscription.subscription-info.cancelation-date') }}:
              </span>
              <span v-b-tooltip.hover="moment(subscription.cancelledAt).format('LLL')">
                {{ moment(subscription.cancelledAt).format('YYYY-MM-DD') }} ({{
                  moment(subscription.cancelledAt).fromNow()
                }})
              </span>
            </p>
          </b-col>
        </b-row>
        <template v-if="isActive">
          <b-row>
            <b-col cols="12">
              <p>
                <span class="mr-25 font-weight-bold">
                  {{ $t('edit-community-modal.subscription.subscription-info.price') }}:
                </span>
                <span>
                  {{ prices.monthly }}
                </span>
              </p>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <b-link
                class="text-primary"
                @click="updatePaymentMethod"
              >
                {{ $t('edit-community-modal.subscription.subscription-info.update-payment-method') }}
              </b-link>
            </b-col>
          </b-row>
        </template>
        <b-row align-h="end">
          <b-col
            cols="4"
            class="d-flex flex-column"
          >
            <template v-if="isActive">
              <b-button
                v-b-tooltip.hover="
                  $t('edit-community-modal.subscription.subscription-info.suspend-subscription-tooltip')
                "
                variant="outline-warning"
                class="mt-1 w-100"
                @click="suspendSubscription"
              >
                <b-spinner
                  v-if="isSuspendSubscriptionLoading"
                  small
                />
                <span v-else>
                  {{ $t('edit-community-modal.subscription.subscription-info.suspend-subscription-action') }}
                </span>
              </b-button>
              <b-button
                v-b-tooltip.hover="
                  $t('edit-community-modal.subscription.subscription-info.cancel-subscription-tooltip')
                "
                variant="outline-danger"
                class="mt-1 w-100"
                @click="cancelSubscription"
              >
                <b-spinner
                  v-if="isCancelSubscriptionLoading"
                  small
                />
                <span v-else>{{
                  $t('edit-community-modal.subscription.subscription-info.cancel-subscription-action')
                }}</span>
              </b-button>
            </template>
            <template v-else-if="isPaused || isCancelled || isPastDue">
              <b-button
                variant="success"
                class="w-100"
                @click="reactivateSubscription"
              >
                <b-spinner
                  v-if="isReactivateSubscriptionLoading"
                  small
                />
                <span v-else>{{
                  $t('edit-community-modal.subscription.subscription-info.resume-subscription-action')
                }}</span>
              </b-button>
            </template>
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <b-row class="d-flex justify-content-center">
          <b-col
            cols="8"
            class="text-center"
          >
            <div class="d-flex justify-content-center">
              <b-spinner
                variant="primary"
                class="my-5"
                size="lg"
              />
            </div>
            <p>{{ $t('edit-community-modal.subscription.getting-started.waiting-for-paiement-confirmation') }}</p>
          </b-col>
        </b-row>
      </div>
    </transition>
    <pause-confirmation
      ref="pauseConfirmation"
      :subscription="subscription"
      @pause-subscription="doSuspendSubscription"
      @error="onError"
    />
    <cancel-confirmation
      ref="cancelConfirmation"
      :subscription="subscription"
      @pause-subscription="doSuspendSubscription"
      @cancel-subscription="doCancelSubscription"
      @error="onError"
    />
  </div>
</template>

<script>
import { BAlert, BBadge, BButton, BCol, BRow, BSpinner, BLink, VBTooltip } from 'bootstrap-vue'
import CancelConfirmation from '@/views/community-management/edit-community-tabs/components/subscription/CancelConfirmation.vue'
import PauseConfirmation from './PauseConfirmation.vue'

export default {
  name: 'SubscriptionInfos',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BRow,
    BSpinner,
    BLink,

    // Modals
    PauseConfirmation,
    CancelConfirmation,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    subscription: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isActionPending: false,
      hasStatusChanged: false,
      isRefreshSubscriptionLoading: false,
      isReactivateSubscriptionLoading: false,
      isSuspendSubscriptionLoading: false,
      isCancelSubscriptionLoading: false,
      prices: {
        unit: null,
        monthly: null,
      },
    }
  },
  computed: {
    isCancelled() {
      return this.subscription.status === this.$constants.SUBSCRIPTION_STATUS.CANCELLED
    },
    isPaused() {
      return (
        this.subscription.status === this.$constants.SUBSCRIPTION_STATUS.PAUSED ||
        (this.subscription.pausedAt && !this.isCancelled && !this.isPastDue)
      )
    },
    isPastDue() {
      return this.subscription.status === this.$constants.SUBSCRIPTION_STATUS.PAST_DUE
    },
    isActive() {
      return this.subscription.status === this.$constants.SUBSCRIPTION_STATUS.ACTIVE && !this.isPaused
    },
    subscriptionStatusBadgeVariant() {
      switch (this.subscription.status) {
        case this.$constants.SUBSCRIPTION_STATUS.ACTIVE:
          if (this.isPaused) {
            return 'warning'
          }
          return 'success'
        case this.$constants.SUBSCRIPTION_STATUS.PAUSED:
          return 'warning'
        case this.$constants.SUBSCRIPTION_STATUS.CANCELLED:
        case this.$constants.SUBSCRIPTION_STATUS.PAST_DUE:
          return 'danger'
        default:
          return 'secondary'
      }
    },
  },
  watch: {
    subscription: {
      handler() {
        this.loadPrices()
      },
      deep: true,
      immediate: true,
    },
    isActionPending() {
      if (!this.isActionPending) {
        this.hasStatusChanged = false
      }
    },
  },
  methods: {
    loadPrices() {
      window.Paddle.Product.Price('net', this.subscription.planId, 1, price => {
        this.prices.unit = price
        this.prices.monthly = price
      })
    },
    stopRefreshSubscriptionLoading() {
      this.isRefreshSubscriptionLoading = false
    },
    refreshSubscription() {
      this.isRefreshSubscriptionLoading = true

      this.$emit('subscription-refreshed')
    },
    updatePaymentMethod() {
      window.Paddle.Checkout.open({
        override: this.subscription.updatePaymentMethodUrl,
        locale: this.$i18n.locale,
      })
    },
    suspendSubscription() {
      this.$bvModal.show('pause-subscription-modal')
    },
    doSuspendSubscription() {
      this.isSuspendSubscriptionLoading = true

      this.$store
        .dispatch('admin/PAUSE_SUBSCRIPTION', {
          pause: true,
        })
        .then(() => {
          this.$bvToast.toast(
            this.$t('edit-community-modal.subscription.subscription-info.subscription-suspended-notification.text'),
            {
              title: this.$t(
                'edit-community-modal.subscription.subscription-info.subscription-suspended-notification.title',
              ),
              variant: 'success',
              solid: true,
            },
          )

          this.$emit('subscription-suspended')

          if (this.hasStatusChanged) {
            this.refreshSubscription()
            this.isActionPending = false
            this.hasStatusChanged = false
          } else {
            this.isActionPending = true
          }
        })
        .catch(error => {
          this.onError(error)
        })
        .finally(() => {
          this.isSuspendSubscriptionLoading = false
        })
    },
    cancelSubscription() {
      this.$bvModal.show('cancel-subscription-modal')
    },
    doCancelSubscription({ cancelReason, cancelReasonDetails }) {
      this.isCancelSubscriptionLoading = true

      this.$store
        .dispatch('admin/CANCEL_SUBSCRIPTION', {
          cancelReason,
          cancelReasonDetails,
        })
        .then(() => {
          this.$bvToast.toast(
            this.$t('edit-community-modal.subscription.subscription-info.subscription-cancelled-notification.text'),
            {
              title: this.$t(
                'edit-community-modal.subscription.subscription-info.subscription-cancelled-notification.title',
              ),
              variant: 'success',
              solid: true,
            },
          )

          this.$emit('subscription-cancelled')

          if (this.hasStatusChanged) {
            this.refreshSubscription()
            this.isActionPending = false
            this.hasStatusChanged = false
          } else {
            this.isActionPending = true
          }
        })
        .catch(error => {
          this.onError(error)
        })
        .finally(() => {
          this.isCancelSubscriptionLoading = false
        })
    },
    reactivateSubscription() {
      if (this.subscription.status === this.$constants.SUBSCRIPTION_STATUS.CANCELLED) {
        if (this.moment(this.subscription.billingCycleEndDate).isAfter(this.moment())) {
          this.$swal({
            title: this.$t(
              'edit-community-modal.subscription.subscription-info.subscription-reactivation-warning.title',
            ),
            text: this.$t('edit-community-modal.subscription.subscription-info.subscription-reactivation-warning.text'),
            icon: 'warning',
            confirmButtonColor: '#375bbf',
            cancelButtonColor: '#ea5455',
            showCancelButton: true,
            confirmButtonText: this.$t(
              'edit-community-modal.subscription.subscription-info.subscription-reactivation-warning.confirm-action',
            ),
            cancelButtonText: this.$t(
              'edit-community-modal.subscription.subscription-info.subscription-reactivation-warning.cancel-action',
            ),
          }).then(result => {
            if (result.value) {
              this.$emit('reactivate-inactive-subscription')
            }
          })
          return
        }

        this.$emit('reactivate-inactive-subscription')
        return
      }

      this.isReactivateSubscriptionLoading = true

      this.$store
        .dispatch('admin/PAUSE_SUBSCRIPTION', {
          pause: false,
        })
        .then(() => {
          this.$bvToast.toast(
            this.$t('edit-community-modal.subscription.subscription-info.subscription-reactivated-notification.text'),
            {
              title: this.$t(
                'edit-community-modal.subscription.subscription-info.subscription-reactivated-notification.title',
              ),
              variant: 'success',
              solid: true,
            },
          )

          this.$emit('subscription-reactivated')

          if (this.hasStatusChanged) {
            this.refreshSubscription()
            this.isActionPending = false
            this.hasStatusChanged = false
          } else {
            this.isActionPending = true
          }
        })
        .catch(error => {
          this.onError(error)
        })
        .finally(() => {
          this.isReactivateSubscriptionLoading = false
        })
    },
    onError(error) {
      this.$emit('error', error)
    },
  },
  sockets: {
    STATUS_CHANGED() {
      this.refreshSubscription()
      this.isActionPending = false
      this.hasStatusChanged = true
    },
  },
}
</script>
